import { useState, useCallback, useEffect } from "react";
import { ShieldCheck, Loader2, Pin, TrendingUp, TrendingDown, Minus, Check, X } from "lucide-react";
import { PostLoadCard, PostLoadCardHeader } from "@/components/post-load/PostLoadCard";
import { PlaceAutocomplete, type PlaceData } from "@/components/ui/place-autocomplete";
import { EquipmentSelect } from "@/components/ui/equipment-select";
import { useSpotRate } from "@/hooks/useSpotRate";
import { haversineDistance } from "@/lib/loadSearch";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getGeocodingSuggestions } from "@/lib/mapbox";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

/* ─── Broker markup bands ─── */
function brokerMarkupPercent(marketRate: number): number {
  if (marketRate <= 2000) return 13;
  if (marketRate <= 4000) return 10;
  if (marketRate <= 6000) return 9;
  if (marketRate <= 9000) return 8;
  return 7;
}

function computeBrokerRate(marketRate: number): number {
  const pct = brokerMarkupPercent(marketRate);
  return Math.round(marketRate * (1 + pct / 100));
}

/* ─── City chip data ─── */
const FALLBACK_ORIGINS = ["Memphis, TN", "Chicago, IL", "Dallas, TX", "Atlanta, GA", "Los Angeles, CA"];
const FALLBACK_DESTINATIONS = ["Chicago, IL", "Atlanta, GA", "Dallas, TX", "Miami, FL", "Bronx, NY"];

function useBrokerCityChips(userId: string | undefined) {
  const [origins, setOrigins] = useState<string[]>(FALLBACK_ORIGINS);
  const [destinations, setDestinations] = useState<string[]>(FALLBACK_DESTINATIONS);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const fetchO = supabase.from("loads").select("origin_city, origin_state").eq("created_by", userId).limit(500);
    const fetchD = supabase.from("loads").select("destination_city, destination_state").eq("created_by", userId).limit(500);

    Promise.all([fetchO, fetchD]).then(([oRes, dRes]) => {
      if (cancelled) return;
      if (oRes.data && oRes.data.length >= 3) {
        const c: Record<string, number> = {};
        for (const r of oRes.data) { const k = `${r.origin_city}, ${r.origin_state}`; c[k] = (c[k] || 0) + 1; }
        const top = Object.entries(c).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k]) => k);
        if (top.length >= 3) setOrigins(top);
      }
      if (dRes.data && dRes.data.length >= 3) {
        const c: Record<string, number> = {};
        for (const r of dRes.data) { const k = `${r.destination_city}, ${r.destination_state}`; c[k] = (c[k] || 0) + 1; }
        const top = Object.entries(c).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k]) => k);
        if (top.length >= 3) setDestinations(top);
      }
    });
    return () => { cancelled = true; };
  }, [userId]);

  return { origins, destinations };
}

/** Resolve "City, ST" to PlaceData using local DB first, Mapbox fallback */
async function resolveChipLocation(label: string): Promise<{ displayLabel: string; placeData: PlaceData } | null> {
  try {
    const { data: localResults } = await supabase.rpc('search_locations', { q: label, lim: 1 });
    if (localResults && localResults.length > 0) {
      const r = localResults[0] as any;
      const placeData: PlaceData = {
        name: `${r.city}, ${r.state_code}`, address: `${r.city}, ${r.state_code}`,
        city: r.city, state: r.state_code, country: 'US', postal_code: '',
        latitude: r.lat || 0, longitude: r.lon || 0,
      };
      return { displayLabel: `${r.city}, ${r.state_code}`, placeData };
    }
    const data = await getGeocodingSuggestions(label);
    const features = data?.features;
    if (!features || features.length === 0) return null;
    const first = features[0];
    const [lng, lat] = first.center || [0, 0];
    const cityCtx = first.context?.find((c: any) => c.id?.startsWith('place'));
    const stateCtx = first.context?.find((c: any) => c.id?.startsWith('region'));
    const city = cityCtx?.text || first.text || '';
    const state = (stateCtx?.short_code || stateCtx?.text || '').replace('US-', '');
    return {
      displayLabel: city && state ? `${city}, ${state}` : first.place_name,
      placeData: { name: first.place_name, address: first.place_name, city: city || first.text, state, country: 'US', postal_code: '', latitude: lat, longitude: lng },
    };
  } catch { return null; }
}

/* ─── Chip sub-components ─── */

function LocationChips({
  suggestions, currentValue, onSelect, onClear,
}: {
  suggestions: string[];
  currentValue: string;
  onSelect: (label: string, placeData: PlaceData) => void;
  onClear: () => void;
}) {
  const [resolving, setResolving] = useState<string | null>(null);

  const handleClick = useCallback(async (chip: string) => {
    if (currentValue === chip) { onClear(); return; }
    setResolving(chip);
    const result = await resolveChipLocation(chip);
    setResolving(null);
    if (result) onSelect(result.displayLabel, result.placeData);
  }, [currentValue, onSelect, onClear]);

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {suggestions.map((s) => {
        const isActive = currentValue === s;
        const isLoading = resolving === s;
        return (
          <button
            key={s} type="button" disabled={isLoading}
            onClick={() => handleClick(s)}
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-medium transition-colors duration-150 border",
              isActive ? "border-primary bg-primary/10 text-primary" : "border-border/40 bg-transparent text-muted-foreground hover:bg-muted/50",
              isLoading && "opacity-50 cursor-wait",
            )}
          >
            {s}
          </button>
        );
      })}
    </div>
  );
}

const EQUIP_CHIPS = [
  { label: "Van", value: "van" },
  { label: "Reefer", value: "reefer" },
  { label: "Flatbed", value: "flatbed" },
];

function EquipChips({ current, onSelect, onClear }: { current: string; onSelect: (v: string) => void; onClear: () => void }) {
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {EQUIP_CHIPS.map((c) => {
        const isActive = current === c.value;
        return (
          <button
            key={c.value} type="button"
            onClick={() => isActive ? onClear() : onSelect(c.value)}
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-medium transition-colors duration-150 border",
              isActive ? "border-primary bg-primary/10 text-primary" : "border-border/40 bg-transparent text-muted-foreground hover:bg-muted/50",
            )}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

/* ─── Trend indicator ─── */
function TrendIndicator({ trend }: { trend: { direction: string; changePercent: number } }) {
  const icon =
    trend.direction === "up" ? <TrendingUp className="h-2.5 w-2.5" /> :
    trend.direction === "down" ? <TrendingDown className="h-2.5 w-2.5" /> :
    <Minus className="h-2.5 w-2.5" />;

  const colorClass =
    trend.direction === "up" ? "text-primary" :
    trend.direction === "down" ? "text-destructive" :
    "text-muted-foreground";

  const label =
    trend.direction === "up" ? `↑ ${trend.changePercent.toFixed(1)}% today` :
    trend.direction === "down" ? `↓ ${trend.changePercent.toFixed(1)}% today` :
    "→ Flat today";

  return (
    <span className={cn("inline-flex items-center gap-1 text-[9px] font-medium", colorClass)}>
      {icon} {label}
    </span>
  );
}

/* ─── Main component ─── */

export default function UnifiedRateGuard() {
  const { user } = useAuth();
  const { origins, destinations } = useBrokerCityChips(user?.id);

  const [origin, setOrigin] = useState("");
  const [originPlace, setOriginPlace] = useState<PlaceData | null>(null);
  const [destination, setDestination] = useState("");
  const [destPlace, setDestPlace] = useState<PlaceData | null>(null);
  const [equipment, setEquipment] = useState("");
  const [pinned, setPinned] = useState(false);
  const [pinning, setPinning] = useState(false);

  // Compute straight-line miles instantly (no API call)
  const laneMiles =
    originPlace && destPlace
      ? Math.round(
          haversineDistance(originPlace.latitude, originPlace.longitude, destPlace.latitude, destPlace.longitude) * 1.15
        )
      : null;

  const { data: spotResult, isLoading } = useSpotRate({
    originLat: originPlace?.latitude,
    originLon: originPlace?.longitude,
    destLat: destPlace?.latitude,
    destLon: destPlace?.longitude,
    laneMiles,
    equipmentType: equipment || null,
  });

  // Check if lane is already tracked when inputs change
  useEffect(() => {
    if (!user || !origin || !destination || !equipment) { setPinned(false); return; }
    let cancelled = false;
    supabase.from("tracked_lanes").select("id").eq("user_id", user.id).eq("origin", origin).eq("destination", destination).eq("equipment_type", equipment).maybeSingle()
      .then(({ data }) => { if (!cancelled) setPinned(!!data); });
    return () => { cancelled = true; };
  }, [user, origin, destination, equipment]);

  const hasInputs = !!(originPlace && destPlace && equipment);
  const hasData = !!spotResult;
  const marketRate = spotResult?.marketRate ?? 0;
  const brokerRate = hasData ? computeBrokerRate(marketRate) : 0;
  const markupPct = hasData ? brokerMarkupPercent(marketRate) : 0;

  const handlePinToTicker = useCallback(async () => {
    if (!user || !origin || !destination || !equipment || pinning) return;
    setPinning(true);
    try {
      const { error } = await supabase.from("tracked_lanes").upsert(
        {
          user_id: user.id,
          origin,
          destination,
          equipment_type: equipment,
          status: "tracked",
        },
        { onConflict: "user_id,origin,destination,equipment_type" }
      );
      if (error) throw error;
      setPinned(true);
      window.dispatchEvent(new CustomEvent("tracked-lane-added", { detail: { origin, destination } }));
      toast.success("Lane pinned to ticker");
    } catch (e: any) {
      console.error("Pin to ticker failed:", e?.message || e);
      toast.error(e?.message || "Failed to pin lane");
    } finally {
      setPinning(false);
    }
  }, [user, origin, destination, equipment, pinning]);

  const handleUnpin = useCallback(async () => {
    if (!user || !origin || !destination || !equipment || pinning) return;
    setPinning(true);
    try {
      const { error } = await supabase
        .from("tracked_lanes")
        .delete()
        .eq("user_id", user.id)
        .eq("origin", origin)
        .eq("destination", destination)
        .eq("equipment_type", equipment);
      if (error) throw error;
      setPinned(false);
      window.dispatchEvent(new CustomEvent("tracked-lane-added", { detail: { origin, destination } }));
      toast.success("Lane unpinned from ticker");
    } catch (e: any) {
      toast.error("Failed to unpin lane");
    } finally {
      setPinning(false);
    }
  }, [user, origin, destination, equipment, pinning]);

  return (
    <PostLoadCard>
      <PostLoadCardHeader
        icon={<ShieldCheck className="h-3.5 w-3.5" style={{ color: "hsl(170 50% 15%)" }} />}
        title="Rate Guard"
        demoHintKey="section.rateGuard"
        variant="atlas"
      />

      <div className="px-4 py-3 space-y-2">
        {/* ─── Origin ─── */}
        <PlaceAutocomplete
          value={origin}
          onChange={(val, pd) => { setOrigin(val); if (pd) setOriginPlace(pd); }}
          placeholder="Origin"
          className="h-8 text-xs"
        />
        <LocationChips
          suggestions={origins}
          currentValue={origin}
          onSelect={(label, pd) => { setOrigin(label); setOriginPlace(pd); }}
          onClear={() => { setOrigin(""); setOriginPlace(null); }}
        />

        {/* ─── Destination ─── */}
        <PlaceAutocomplete
          value={destination}
          onChange={(val, pd) => { setDestination(val); if (pd) setDestPlace(pd); }}
          placeholder="Destination"
          className="h-8 text-xs"
        />
        <LocationChips
          suggestions={destinations}
          currentValue={destination}
          onSelect={(label, pd) => { setDestination(label); setDestPlace(pd); }}
          onClear={() => { setDestination(""); setDestPlace(null); }}
        />

        {/* ─── Equipment ─── */}
        <EquipmentSelect
          value={equipment}
          onChange={(v) => setEquipment(v as string)}
          mode="single"
          placeholder="Equipment"
          className="h-8 text-xs"
        />
        <EquipChips
          current={equipment}
          onSelect={setEquipment}
          onClear={() => setEquipment("")}
        />

        {/* ─── Output area ─── */}
        <div
          className="rounded-lg p-3 mt-1"
          style={{ backgroundColor: "hsl(var(--brand-teal-deep) / 0.06)" }}
        >
          {!hasInputs ? (
            <p className="text-xs text-muted-foreground text-center py-2">
              Enter origin, destination &amp; equipment to check rates
            </p>
          ) : isLoading ? (
            <div className="flex items-center gap-2 py-3 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" style={{ color: "hsl(var(--brand-teal-deep))" }} />
              <span className="text-xs text-muted-foreground">Analyzing lane comps…</span>
            </div>
          ) : hasData ? (
            <div className="space-y-3">
              {/* Direct to Shipper */}
              <div>
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Direct to Shipper</span>
                <p className="text-2xl font-extrabold tracking-tight mt-0.5" style={{ color: "hsl(var(--brand-teal-deep))" }}>
                  ${marketRate.toLocaleString()}
                </p>
                {/* Compact pills */}
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <span className="inline-flex items-center rounded-full bg-muted/60 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground">
                    {spotResult.compCount} comps
                  </span>
                  <span className="inline-flex items-center rounded-full bg-muted/60 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground">
                    ${spotResult.marketRpm.toFixed(2)}/mi
                  </span>
                  <span className="inline-flex items-center rounded-full bg-muted/60 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground">
                    Tier {spotResult.tier}
                  </span>
                  {spotResult.trend && <TrendIndicator trend={spotResult.trend} />}
                </div>
              </div>

              <div className="border-t border-border" />

              {/* Fair Broker Rate */}
              <div>
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Fair Broker Rate</span>
                <div className="flex items-baseline gap-2 mt-0.5">
                  <p className="text-2xl font-extrabold tracking-tight text-foreground">${brokerRate.toLocaleString()}</p>
                  <span
                    className="text-xs font-semibold rounded-full px-2 py-0.5"
                    style={{ backgroundColor: "hsl(var(--brand-teal-subtle))", color: "hsl(var(--brand-teal-deep))" }}
                  >
                    +{markupPct}%
                  </span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5">Recommended posting rate with negotiation room</p>
              </div>

              {/* Pin to Ticker */}
              {user && (
                <button
                  type="button"
                  disabled={pinning}
                  onClick={pinned ? handleUnpin : handlePinToTicker}
                  className={cn(
                    "w-full flex items-center justify-center gap-1.5 rounded-md py-1.5 text-[10px] font-medium transition-colors border",
                    pinned
                      ? "border-destructive/30 bg-destructive/5 text-destructive hover:bg-destructive/10"
                      : "border-border/50 bg-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                  )}
                >
                  {pinning ? (
                    <><Loader2 className="h-3 w-3 animate-spin" /> {pinned ? "Unpinning…" : "Pinning…"}</>
                  ) : pinned ? (
                    <><X className="h-3 w-3" /> Unpin from Ticker</>
                  ) : (
                    <><Pin className="h-3 w-3" /> Pin to Ticker</>
                  )}
                </button>
              )}
            </div>
          ) : (
            <div className="py-2 text-center">
              <p className="text-sm font-semibold text-muted-foreground">Not enough data yet</p>
              <p className="text-[10px] text-muted-foreground/70 mt-1">Please check back soon — we're gathering more data as we grow.</p>
            </div>
          )}
        </div>
      </div>
    </PostLoadCard>
  );
}